import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    FiPlus,
    FiEdit2,
    FiTrash2,
    FiCheck,
    FiX,
    FiFilter,
    FiLink,
    FiType,
    FiBox,
    FiServer,
    FiGlobe,
    FiFile,
    FiCheckSquare,
    FiSquare,
    FiAlertCircle,
    FiSearch
} from "react-icons/fi";
import axios from "../../../utils/api/axios";
import { toast } from "react-hot-toast";
import AddResourceModal from "./AddResourceModal";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";

interface ResourceManagerProps {
    assetId: string;
    targetId: string;
    onClose: () => void;
    readOnly?: boolean;
}

interface TargetEndpointResource {
    resource_id: number;
    target_id: string;
    value: string;
    resource_type: "PATH" | "PARAMETER" | "HEADER" | "IP" | "PORT" | "FILE" | "OTHER";
    status: "IN_SCOPE" | "OUT_OF_SCOPE" | "TESTED" | "PENDING" | "EXCLUDED";
    discovery_method: "MANUAL" | "IMPORT" | "PENTESTER" | "AI_DISCOVERY";
    http_method?: string | null;
    notes?: string | null;
    program_id?: number | null;
    created_by?: number;
    created_at: string;
    creator?: {
        user_id: number;
        username: string;
        display_name: string;
        email: string;
    };
}

const ResourceManager: React.FC<ResourceManagerProps> = ({ assetId, targetId, onClose, readOnly = false }) => {
    const [resources, setResources] = useState<TargetEndpointResource[]>([]);
    const [loading, setLoading] = useState(true);
    const [targetType, setTargetType] = useState<string>("");
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [filterStatus, setFilterStatus] = useState<string>("");
    const [filterType, setFilterType] = useState<string>("");
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingResource, setEditingResource] = useState<TargetEndpointResource | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    const { role: userRole, id: currentUserId } = useUserCredentials();
    const isAdminOrBusiness = userRole === UserRole.ADMIN || userRole === UserRole.BUSINESS || userRole === UserRole.SUB_ADMIN || userRole === UserRole.BUSINESS_ADMINISTRATOR;
    const isDeveloper = userRole === UserRole.DEVELOPER;
    const isResearcher = userRole === UserRole.RESEARCHER;

    const fetchTarget = useCallback(async () => {
        try {
            const { data } = await axios.get(`/assets/${assetId}/targets/${targetId}`);
            setTargetType(data.target_type || "");
        } catch (error) {
            console.error("Error fetching target:", error);
        }
    }, [assetId, targetId]);

    const fetchResources = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`/assets/${assetId}/targets/${targetId}/resources`, {
                params: {
                    status: filterStatus || undefined,
                    type: filterType || undefined
                }
            });
            setResources(data);
            setSelectedIds([]); // Clear selection on refresh
        } catch (error: any) {
            console.error("Error fetching resources:", error);
            toast.error("Failed to load endpoints");
        } finally {
            setLoading(false);
        }
    }, [assetId, targetId, filterStatus, filterType]);

    // Fetch resources and target on mount
    useEffect(() => {
        fetchResources();
        fetchTarget();
    }, [fetchResources, fetchTarget]);

    const handleDelete = async (resourceId: number) => {
        if (!window.confirm("Are you sure you want to delete this endpoint?")) return;

        try {
            await axios.delete(`/assets/${assetId}/targets/${targetId}/resources/${resourceId}`);
            toast.success("Endpoint deleted");
            fetchResources();
        } catch (error: any) {
            console.error("Error deleting resource:", error);
            toast.error("Failed to delete endpoint");
        }
    };

    const handleBulkAction = async (action: "approve" | "delete") => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Are you sure you want to ${action} ${selectedIds.length} items?`)) return;

        try {
            setLoading(true);
            await axios.post(`/assets/${assetId}/targets/${targetId}/resources/bulk-action`, {
                resourceIds: selectedIds,
                action
            });
            toast.success(`Bulk ${action} successful`);
            fetchResources();
        } catch (error: any) {
            console.error(`Error during bulk ${action}:`, error);
            toast.error(`Bulk ${action} failed`);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelect = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === resources.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(resources.map(r => r.resource_id));
        }
    };

    const getResourceIcon = (type: string) => {
        const icons: Record<string, JSX.Element> = {
            PATH: <FiLink className="w-4 h-4" />,
            PARAMETER: <FiType className="w-4 h-4" />,
            HEADER: <FiBox className="w-4 h-4" />,
            IP: <FiGlobe className="w-4 h-4" />,
            PORT: <FiServer className="w-4 h-4" />,
            FILE: <FiFile className="w-4 h-4" />,
            OTHER: <FiBox className="w-4 h-4" />
        };
        return icons[type] || icons.OTHER;
    };

    const getStatusBadge = (status: string) => {
        const styles: Record<string, string> = {
            IN_SCOPE: "bg-green-100 text-green-700",
            OUT_OF_SCOPE: "bg-gray-100 text-gray-700",
            TESTED: "bg-blue-100 text-blue-700",
            PENDING: "bg-green-100 text-green-700",
            EXCLUDED: "bg-red-100 text-red-700"
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.IN_SCOPE}`}>
                {status === 'PENDING' ? 'IN SCOPE' : status.replace(/_/g, " ")}
            </span>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-[1.5rem] w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] border border-slate-200"
            >
                {/* Header */}
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-white relative">
                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-600 rounded-r-full" />
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold text-slate-900 tracking-tight">Endpoint Governance</h2>
                        </div>
                        <p className="text-[11px] text-slate-400 font-medium">Domain: <span className="text-slate-900 font-bold">{targetType}</span></p>
                    </div>
                    <div className="flex items-center gap-3">
                        {!readOnly && (
                            <button
                                onClick={() => setShowAddModal(true)}
                                className="bg-blue-600 text-white px-4 py-2 rounded-xl font-medium flex items-center gap-2 hover:bg-blue-700 transition-all shadow-lg shadow-blue-100"
                            >
                                <FiPlus /> Add Endpoint
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                        >
                            <FiX className="w-6 h-6 text-slate-400" />
                        </button>
                    </div>
                </div>

                {/* Filters & Bulk Header */}
                <div className="px-5 py-2 border-b border-slate-100 flex items-center gap-2 bg-slate-50/40">
                    <div className="flex items-center gap-2 mr-1">
                        <button
                            onClick={toggleSelectAll}
                            className="text-slate-300 hover:text-blue-600 transition-all transform active:scale-95"
                            title="Select All"
                        >
                            {selectedIds.length === resources.length && resources.length > 0 ? (
                                <FiCheckSquare className="w-4 h-4 text-blue-600" />
                            ) : (
                                <FiSquare className="w-4 h-4" />
                            )}
                        </button>
                    </div>

                    <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                        <FiFilter className="w-3 h-3 text-slate-400" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="text-[11px] font-bold text-slate-700 outline-none bg-transparent cursor-pointer min-w-[90px]"
                        >
                            <option value="IN_SCOPE">In Scope</option>
                            <option value="TESTED">Tested</option>
                            <option value="OUT_OF_SCOPE">Out of Scope</option>
                            <option value="EXCLUDED">Excluded</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                        <FiBox className="w-3 h-3 text-slate-400" />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="text-[11px] font-bold text-slate-700 outline-none bg-transparent cursor-pointer min-w-[90px]"
                        >
                            <option value="">All Types</option>
                            <option value="PATH">Path</option>
                            <option value="IP">IP</option>
                            <option value="PORT">Port</option>
                            <option value="PARAMETER">Parameter</option>
                            <option value="HEADER">Header</option>
                            <option value="FILE">File</option>
                        </select>
                    </div>

                    <div className="relative flex-1 max-w-[200px] ml-1">
                        <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                        <input
                            type="text"
                            placeholder="Find..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-8 pr-3 py-1 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none bg-white shadow-sm transition-all"
                        />
                    </div>

                    <span className="text-[10px] font-black text-slate-400 ml-auto uppercase tracking-tighter bg-slate-100 px-2 py-1 rounded">
                        {resources.length} Total
                    </span>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 relative bg-slate-50/10">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-slate-500">Loading endpoints...</p>
                        </div>
                    ) : resources.length === 0 ? (
                        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-2xl">
                            <FiLink className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                            <p className="text-slate-600 font-medium mb-2">No endpoints defined</p>
                            <p className="text-sm text-slate-400 mb-6">{readOnly ? "No endpoints defined for this target." : "Add your first endpoint to define the testing scope"}</p>
                            {!readOnly && (
                                <button
                                    onClick={() => setShowAddModal(true)}
                                    className="bg-blue-600 text-white px-6 py-3 rounded-xl font-medium inline-flex items-center gap-2 hover:bg-blue-700 transition-all"
                                >
                                    <FiPlus /> Add First Endpoint
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {resources.filter(r => r.value.toLowerCase().includes(searchTerm.toLowerCase())).map((resource) => (
                                <div
                                    key={resource.resource_id}
                                    className={`p-3 border rounded-xl transition-all group relative ${selectedIds.includes(resource.resource_id) ? "border-blue-300 bg-blue-50/30 shadow-sm" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-4 flex-1">
                                            <button
                                                onClick={() => toggleSelect(resource.resource_id)}
                                                className={`mt-3 transition-colors ${selectedIds.includes(resource.resource_id) ? "text-blue-600" : "text-slate-300 hover:text-slate-400"}`}
                                            >
                                                {selectedIds.includes(resource.resource_id) ? (
                                                    <FiCheckSquare className="w-5 h-5" />
                                                ) : (
                                                    <FiSquare className="w-5 h-5" />
                                                )}
                                            </button>

                                            <div className={`p-2 rounded-lg transition-colors ${selectedIds.includes(resource.resource_id) ? "bg-blue-100 text-blue-600" : "bg-slate-100 group-hover:bg-slate-200"}`}>
                                                {getResourceIcon(resource.resource_type)}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-mono text-slate-900 font-medium text-sm">{resource.value}</span>
                                                    {resource.http_method && (
                                                        <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-black rounded border border-indigo-100">
                                                            {resource.http_method}
                                                        </span>
                                                    )}
                                                    {getStatusBadge(resource.status)}
                                                </div>
                                                <div className="flex items-center gap-3 text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
                                                    <span className="flex items-center gap-1">
                                                        <FiType className="w-2.5 h-2.5" />
                                                        {resource.resource_type}
                                                    </span>
                                                    {resource.discovery_method && (
                                                        <span className="flex items-center gap-1">
                                                            {resource.discovery_method === "PENTESTER" ? "🔍" : "✋"}
                                                            {resource.discovery_method}
                                                        </span>
                                                    )}
                                                    {resource.creator && (
                                                        <span className="text-slate-500">
                                                            • {resource.creator.display_name || resource.creator.username}
                                                        </span>
                                                    )}
                                                </div>
                                                {resource.notes && (
                                                    <p className="text-sm text-slate-600 mt-2 italic">{resource.notes}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {!readOnly && (
                                                <button
                                                    onClick={() => setEditingResource(resource)}
                                                    className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <FiEdit2 className="w-4 h-4" />
                                                </button>
                                            )}
                                            {isAdminOrBusiness && !readOnly && (
                                                <button
                                                    onClick={() => handleDelete(resource.resource_id)}
                                                    className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                                                    title="Delete"
                                                >
                                                    <FiTrash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Bulk Action Bar - Hide if readOnly */}
                    {!readOnly && (
                        <AnimatePresence>
                            {selectedIds.length > 0 && (
                                <motion.div
                                    initial={{ y: 100, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: 100, opacity: 0 }}
                                    className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-8 min-w-[400px]"
                                >
                                    <span className="text-sm font-medium">
                                        {selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected
                                    </span>
                                    <div className="h-6 w-px bg-slate-700" />
                                    <div className="flex items-center gap-3">
                                        {isAdminOrBusiness && (
                                            <button
                                                onClick={() => handleBulkAction("delete")}
                                                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-xl text-sm font-bold transition-all"
                                            >
                                                <FiTrash2 /> Delete
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setSelectedIds([])}
                                            className="p-2 hover:bg-slate-800 rounded-xl transition-colors"
                                            title="Cancel Selection"
                                        >
                                            <FiX />
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    )}
                </div>
            </motion.div>

            {/* Edit Modal Component */}
            {editingResource && (
                <EditResourceModal
                    resource={editingResource}
                    isAdminOrBusiness={isAdminOrBusiness}
                    isDeveloper={isDeveloper}
                    isResearcher={isResearcher}
                    currentUserId={currentUserId}
                    onClose={() => setEditingResource(null)}
                    onSuccess={() => {
                        setEditingResource(null);
                        fetchResources();
                    }}
                    assetId={assetId}
                    targetId={targetId}
                />
            )}

            {/* Add Modal */}
            {showAddModal && (
                <AddResourceModal
                    assetId={assetId}
                    targetId={targetId}
                    targetType={targetType}
                    onClose={() => setShowAddModal(false)}
                    onSuccess={fetchResources}
                />
            )}
        </div>
    );
};

const EditResourceModal: React.FC<{
    resource: TargetEndpointResource;
    isAdminOrBusiness: boolean;
    isDeveloper: boolean;
    isResearcher: boolean;
    currentUserId: number | undefined;
    onClose: () => void;
    onSuccess: () => void;
    assetId: string;
    targetId: string;
}> = ({ resource, isAdminOrBusiness, isDeveloper, isResearcher, currentUserId, onClose, onSuccess, assetId, targetId }) => {
    const [notes, setNotes] = useState(resource.notes || "");
    const [status, setStatus] = useState(resource.status);
    const [value, setValue] = useState(resource.value);
    const [loading, setLoading] = useState(false);

    const canEditValue = isAdminOrBusiness && !resource.program_id;
    const canEditStatus = isAdminOrBusiness || isDeveloper;
    const canEditNotes = isAdminOrBusiness || isDeveloper || (isResearcher && resource.created_by === currentUserId);

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const updates: any = {};
            if (canEditNotes) updates.notes = notes;
            if (canEditStatus) updates.status = status;
            if (canEditValue) updates.value = value;

            await axios.put(`/assets/${assetId}/targets/${targetId}/resources/${resource.resource_id}`, updates);
            toast.success("Endpoint updated");
            onSuccess();
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Update failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md">
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
            >
                <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-xl font-bold text-slate-900">Edit Endpoint</h3>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full">
                        <FiX />
                    </button>
                </div>
                <form onSubmit={handleUpdate} className="p-8 space-y-6">
                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Endpoint Value</label>
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            disabled={!canEditValue}
                            className={`w-full px-4 py-3 border rounded-xl mt-1 font-medium ${!canEditValue ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-white focus:ring-2 focus:ring-blue-500'}`}
                        />
                        {resource.program_id && (
                            <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                                <FiAlertCircle className="w-3 h-3" /> Technical value is locked for program discoveries
                            </p>
                        )}
                    </div>

                    {canEditStatus && (
                        <div>
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Status</label>
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value as any)}
                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                            >
                                <option value="IN_SCOPE">In Scope</option>
                                <option value="TESTED">Tested</option>
                                <option value="OUT_OF_SCOPE">Out of Scope</option>
                                <option value="EXCLUDED">Excluded</option>
                            </select>
                        </div>
                    )}

                    <div>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={!canEditNotes}
                            rows={3}
                            className={`w-full px-4 py-3 border rounded-xl mt-1 font-medium resize-none ${!canEditNotes ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-white focus:ring-2 focus:ring-blue-500'}`}
                            placeholder="Add your notes here..."
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button type="button" onClick={onClose} className="px-6 py-2 text-slate-500 font-bold hover:bg-slate-50 rounded-xl">Cancel</button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-8 py-2 bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-100 hover:bg-blue-700 disabled:opacity-50"
                        >
                            {loading ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default ResourceManager;
